/**
 * Pure pair-allocation math, mirroring fortuna-by-code-be/src/pair/pair-allocator.service.ts.
 *
 * Used by the place-pair form to render a live "if you allocate X to stock and Y
 * to deriv, here is the resulting max notional and remaining idle cash" preview
 * before the user submits. The authoritative balance after placement comes from
 * GET /pair/allocation/:dataConfigId/balance — this is calculator-only.
 *
 * Direction matters. We do NOT take a margin rate and derive how much money to
 * commit; we take the collateral that is actually there, work out how many
 * contracts it carries at the broker's floor, and report the rate that implies.
 * The floor's only power is to refuse. There is no configurable rate and no
 * buffer ratio — how much cushion the deriv leg carries is decided by how much
 * cash the user funds it with.
 *
 * All amounts are raw VND. Default constants match docs/pinetree-api-external-derivative.md
 * §"Constants" (VN30F: floor 22.7%, fee 7,250/contract, multiplier 100,000).
 */

import { formatPercent } from './numberFormat';

/** Broker floor for VN30F. Overridden per broker on the BE (`broker-margin.ts`). */
export const DEFAULT_DERIV_MIN_MARGIN_RATE = 0.227;
export const DEFAULT_DERIV_FEE_PER_CONTRACT = 7_250;
export const DEFAULT_DERIV_MULTIPLIER = 100_000;

export interface PairAllocationInput {
    stockAllocatedCash: number;
    derivAllocatedCash: number;
    /** Futures mark price in POINTS. Required: the reverse direction cannot size without it. */
    derivPrice?: number;
    derivMultiplier?: number;
    /** Broker's floor margin rate. */
    minMarginRate?: number;
    feePerContract?: number;
}

export interface PairAllocationPreview {
    stockMaxNotional: number;
    derivMaxNotional: number;
    pairNotional: number;
    bottleneckLeg: 'STOCK' | 'DERIV';
    derivQty: number;
    derivMarginNeeded: number;
    derivIdleAfter: number;
    stockIdleAfter: number;
    /**
     * Rate `derivAllocatedCash` works out to for the sized notional. Equals the
     * floor when the deriv leg is the bottleneck (it is sized to consume the cash);
     * sits ABOVE the floor when the stock leg binds first, which is the headroom
     * the user actually has.
     */
    impliedMarginRate: number;
    warnings: string[];
}

/**
 * Compute the pair allocation from the user's intended cash splits and the current
 * futures price.
 *
 * Sizing model (NOTIONAL_MATCH):
 *   perContractAtFloor = derivPrice × multiplier × minMarginRate + feePerContract
 *   derivQty           = floor(derivAllocatedCash / perContractAtFloor)
 *   derivMaxNotional   = derivQty × derivPrice × multiplier
 *   stockMaxNotional   = stockAllocatedCash                 (1:1 for cash equity)
 *   pairNotional       = min(stock, deriv)
 *   impliedMarginRate  = (derivAllocatedCash − qty × fee) / pairNotional
 *
 * Without `derivPrice` there is nothing to report: the reverse direction needs a
 * notional to divide by. That is a real change from the forward model, which could
 * bound by collateral alone.
 */
export function computePairAllocationPreview(input: PairAllocationInput): PairAllocationPreview {
    const stockAlloc = Math.max(input.stockAllocatedCash, 0);
    const derivAlloc = Math.max(input.derivAllocatedCash, 0);
    const floor = input.minMarginRate ?? DEFAULT_DERIV_MIN_MARGIN_RATE;
    const multiplier = input.derivMultiplier ?? DEFAULT_DERIV_MULTIPLIER;
    const fee = input.feePerContract ?? DEFAULT_DERIV_FEE_PER_CONTRACT;
    const price = input.derivPrice ?? 0;

    const warnings: string[] = [];
    if (floor <= 0) {
        warnings.push('minMarginRate must be > 0');
    }
    if (price <= 0) {
        warnings.push('no futures price — cannot size the deriv leg');
    }

    const notionalPerContract = price * multiplier;
    const perContractAtFloor = notionalPerContract * floor + fee;

    let derivQty = 0;
    if (perContractAtFloor > 0 && notionalPerContract > 0) {
        derivQty = Math.floor(derivAlloc / perContractAtFloor);
    }
    const derivMaxNotional = derivQty * notionalPerContract;
    const stockMaxNotional = stockAlloc;

    const pairNotional = Math.min(stockMaxNotional, derivMaxNotional);
    const bottleneckLeg: 'STOCK' | 'DERIV' = stockMaxNotional <= derivMaxNotional ? 'STOCK' : 'DERIV';

    // Contracts actually used once the stock leg's cap is applied.
    const usedQty =
        notionalPerContract > 0 ? Math.floor(pairNotional / notionalPerContract) : 0;
    const derivMarginNeeded = usedQty * perContractAtFloor;
    const usedNotional = usedQty * notionalPerContract;
    const impliedMarginRate =
        usedNotional > 0 ? (derivAlloc - usedQty * fee) / usedNotional : 0;

    const derivIdleAfter = derivAlloc - derivMarginNeeded;
    const stockIdleAfter = stockAlloc - pairNotional;

    if (pairNotional <= 0) {
        warnings.push('at least one leg cannot fund a single contract — no headroom');
    } else if (impliedMarginRate < floor) {
        // Shouldn't happen (qty is floored at the floor rate) but a bad price or
        // fee override could get here, and silently under-margining is worse.
        warnings.push(
            `collateral implies ${formatPercent(impliedMarginRate * 100)} margin, below the ` +
                `${formatPercent(floor * 100)} the broker requires — the bot will refuse to open`,
        );
    }

    return {
        stockMaxNotional,
        derivMaxNotional,
        pairNotional,
        bottleneckLeg,
        derivQty,
        derivMarginNeeded,
        derivIdleAfter,
        stockIdleAfter,
        impliedMarginRate,
        warnings,
    };
}
